import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS, QUIZ_HINT_COST_POINTS, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { rubricPointsToScoreDelta, formatRubricAsGamePointsLabel } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";
import "./Scenario3.css";

const SC3_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
const SC3_HINT_THEME_CLASS = `${SC3_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();
const SC3_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"¿Cuál debería ser el primer piloto de IA que ponga en marcha el comité?";

const RETROALIMENTACION =
	"Automatizar informes internos permite empezar con un caso útil, medible y de riesgo controlado. El equipo podrá comparar tiempos, calidad del resultado y nivel de adopción antes de escalar la IA a procesos más sensibles.";

const HINT_HTML = `<div class="txtTexto1 mb-2">El primer piloto debe ser suficientemente útil para demostrar valor, pero también acotado, medible y seguro. Buscad una propuesta interna, con revisión humana y con resultados fáciles de comparar antes y después.</div>`;

const FINALIST_PROPOSALS = [
	{
		label: "A",
		title: "Automatización de informes internos",
		description:
			"Genera borradores de informes comerciales a partir de datos internos, con revisión humana.",
		impacto: "medio-alto",
		viabilidad: "alta",
		riesgo: "bajo",
		medicion: "alta",
	},
	{
		label: "B",
		title: "Análisis de opiniones de clientes",
		description: "Analiza reseñas, comentarios y encuestas para detectar patrones.",
		impacto: "medio",
		viabilidad: "media",
		riesgo: "medio",
		medicion: "media",
	},
	{
		label: "C",
		title: "Clasificación automática de emails internos",
		description: "Ordena correos por temática, urgencia o departamento.",
		impacto: "medio",
		viabilidad: "alta",
		riesgo: "bajo",
		medicion: "media",
	},
	{
		label: "D",
		title: "Predicción de demanda",
		description: "Anticipa picos de demanda a partir de datos históricos.",
		impacto: "alto",
		viabilidad: "media",
		riesgo: "medio",
		medicion: "alta",
	},
	{
		label: "E",
		title: "Asistente interno de búsqueda documental",
		description:
			"Ayuda a empleados a encontrar documentación, procedimientos y respuestas internas.",
		impacto: "medio-alto",
		viabilidad: "media",
		riesgo: "medio",
		medicion: "media",
	},
];

const SCENARIO3_QUIZ2_BASE_OPTIONS = [
	{
		id: 1,
		label: "A",
		text: "Automatización de informes internos",
		points: 100,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		label: "B",
		text: "Análisis de opiniones de clientes",
		points: 50,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		label: "C",
		text: "Clasificación automática de emails internos",
		points: 50,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		label: "D",
		text: "Predicción de demanda",
		points: 50,
		img: null,
		explanation: "",
	},
	{
		id: 5,
		label: "E",
		text: "Asistente interno de búsqueda documental",
		points: 50,
		img: null,
		explanation: "",
	},
];

function Scenario3Quiz2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.SCENARIO3_VERSIONS_COMPARE_QUIZ;
	const SCENARIO_ID = SCENARIOS.SCENARIO_3;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "violet");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO3_QUIZ2_BASE_OPTIONS), []);

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO3_FIN,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO3_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO3_FIN);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC3_HINT_MARK_HTML,
				color: "violet",
				big: false,
				themePopupClass: SC3_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC3_HINT_MARK_HTML,
			color: "violet",
			big: false,
			themePopupClass: SC3_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(QUIZ_HINT_COST_POINTS);
		}
	};

	function checkAnswer() {
		if (!selectedOption) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 violet">Debes seleccionar una opción</div>`,
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});
			return;
		}

		const option = displayOptions.find((o) => o.id === selectedOption);
		if (!option) return;

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const rubricPass = option.points >= QUIZ_RUBRIC_PASS_POINTS;
			const scoreDelta = rubricPointsToScoreDelta(option.points, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA);
			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = scoreDelta - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
			const outcomeStyle = rubricPass ? "correct" : "incorrect";

			modalQuestionResolution({
				correct: rubricPass,
				question: `${option.label}. ${option.text}`,
				textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "violet")}
                      <div class="txt28 bold pt-4">${formatRubricAsGamePointsLabel(option.points)}</div>
                      <div class="txt22 mt-2">${rubricPass ? "Has acertado." : "No has acertado."}</div>
                      ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "violet",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, rubricPass, { rubricPoints: option.points });
			});
		});
	}

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz sc3-quiz2-pilot-choice">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero sc3-quiz2-pilot-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 3 · Evolución
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem sc3-quiz2-pilot-stem">{QUESTION_STEM}</p>
					</header>

					<section className="sc3-quiz2-proposals-panel" aria-label="Propuestas finalistas">
						<div className="sc3-quiz2-proposals-grid">
							{FINALIST_PROPOSALS.map((proposal) => (
								<article key={proposal.label} className="sc3-quiz2-proposal-card">
									<h3 className="sc3-quiz2-proposal-title">
										<span className="sc3-quiz2-proposal-letter">{proposal.label}</span>
										{proposal.title}
									</h3>
									<p className="sc3-quiz2-proposal-desc">{proposal.description}</p>
									<dl className="sc3-quiz2-proposal-metrics">
										<div>
											<dt>Impacto</dt>
											<dd>{proposal.impacto}</dd>
										</div>
										<div>
											<dt>Viabilidad</dt>
											<dd>{proposal.viabilidad}</dd>
										</div>
										<div>
											<dt>Riesgo</dt>
											<dd>{proposal.riesgo}</dd>
										</div>
										<div>
											<dt>Medición</dt>
											<dd>{proposal.medicion}</dd>
										</div>
									</dl>
								</article>
							))}
						</div>
					</section>

					<div className="sc2-quiz-toolbar sc3-quiz2-pilot-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={24}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Consulta la tabla y elige la opción que mejor encaje como primer piloto. Cada respuesta
								suma una puntuación distinta.
							</span>
						</p>
					</div>

					<div className="sc2-quiz-options sc3-quiz2-pilot-options">
						{displayOptions.map((option) => {
							const isSelected = selectedOption === option.id;
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""}`}
									onClick={() => setSelectedOption(option.id)}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}`}
								>
									<div className="sc2-quiz-option-row">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text">{option.text}</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario3Quiz2;
